import { FC, useCallback, useMemo, useState } from 'react'
import SuccessI from '@mui/icons-material/CheckCircle'
import { Box } from '@mui/material'
import { UploadedFile } from 'models/general'
import { NPSFileResponse } from 'models/management.models'
import { IManagementAccountantsSearchForm } from 'lib/forms/management/management.form.models'
import { selectSelectedWorkspace } from 'lib/helpers/selectors'
import useDialog from 'hooks/Shared/useDialog'
import { useFetchNpsOcr, useUploadNPSFile } from 'hooks/queries/management'
import { useAppSelector } from 'store/hooks'
import { useManagementAccountantsContainerContext } from 'containers/Management/ManagementAccountants/ManagementAccountantsContainer/ManagementAccountantsContainer'
import { TModalIcon } from 'components/Shared/SydDialog/SydDialog'
import ManagementAccountantsNPSButton from '../ManagementAccountantsNPSButton/ManagementAccountantsNPSButton'
import ManagementAccountantsSearch from '../ManagementAccountantsSearch/ManagementAccountantsSearch'
import './styles.scss'

const ManagementAccountantsNPSSection: FC = () => {
  const workspace = useAppSelector(selectSelectedWorkspace)

  const wid = useMemo<number>(
    () => (workspace !== null ? workspace.id : 0),
    [workspace]
  )
  const [isOpen, setIsOpen] = useState(false)
  const [file, setFile] = useState<UploadedFile | undefined>()

  const { refetchManagementAccountants, updateManagementAccountantsState } =
    useManagementAccountantsContainerContext()

  const dialog = useDialog()

  const { mutate: fetchNpsOcr, isLoading: npsOcrIsLoading } = useFetchNpsOcr(
    wid,
    {
      onSuccess: async data => {
        setIsOpen(false)
        setFile(undefined)
        dialog.primary({
          title: 'Exitoso!',
          subtitle: 'Se subió correctamente el archivo NPS.',
          icon: SuccessI as TModalIcon,
          primaryButtonText: 'OK',
        })

        await refetchManagementAccountants()
      },
    }
  )

  const { mutate: uploadNPSFileMutate, isLoading } = useUploadNPSFile(wid, {
    onSuccess: (data: NPSFileResponse) => {
      fetchNpsOcr(data.uuid)
    },
  })

  const handleSubmit = useCallback(
    (file: UploadedFile) => {
      const base64 = file.base64
      const splitBase64 = base64.split(',')
      uploadNPSFileMutate({
        documents: [
          {
            file: splitBase64[1],
            name: file.name,
          },
        ],
      })
    },
    [uploadNPSFileMutate]
  )

  const onSubmit = useCallback(
    async ({ search }: IManagementAccountantsSearchForm) => {
      updateManagementAccountantsState({ param: search })
    },
    [updateManagementAccountantsState]
  )

  return (
    <Box
      display="flex"
      justifyContent="space-between"
      alignItems={'center'}
      marginBottom={4}
      className="management-accountants-nps-section">
      <ManagementAccountantsSearch onSubmit={onSubmit} />
      <ManagementAccountantsNPSButton
        handleSubmit={handleSubmit}
        isLoading={isLoading || npsOcrIsLoading}
        isOpen={isOpen}
        setIsOpen={setIsOpen}
        setFile={setFile}
        file={file}
      />
    </Box>
  )
}

export default ManagementAccountantsNPSSection
